import { useCallCenterRoute, useCallCenterRouteOrders } from "api/call-center/routes/hooks";
import { CallCenterRouteDetails } from "api/call-center/routes/models";
import { HighlightedRow } from "api/other/models";
import { UUID } from "api/types";
import { InvoicePagination } from "components/common/invoicePagination/InvoicePagination";
import { CommonError, RightPanelHandler } from "components/utils";
import { ListDrawerWrapper } from "components/utils/drawer";
import { Loader } from "components/utils/loader";
import { ModuleList } from "components/utils/moduleList";
import { ModuleListBody } from "components/utils/moduleList/components/ModuleListBody";
import { ModuleListHeader } from "components/utils/moduleList/components/ModuleListHeader";
import { callCenterSingleRouteOrdersListConfig } from "components/utils/moduleList/utils/moduleListConfig";
import { NoResults } from "components/utils/noResults";
import { useQuery } from "hooks";
import { useEffect } from "react";
import { useState } from "react";
import { useLocation, useParams } from "react-router";
import { handleMultipleRowsHighlight } from "utilities/handleMultipleRowsHighlight";
import { ListHeader } from "./components/ListHeader";
import { ListItem } from "./components/ListItem";
import { getSearch } from "./utils/getSearch";
import { CallInProgressPopUp } from "./components/CallInProgressPopUp";
import { RightPanel } from "./rightPanel/RightPanel";

type LocationState = { route: CallCenterRouteDetails } | undefined;

export const RouteOrdersList = () => {
  const [highlightedRows, setHighlightedRows] = useState<HighlightedRow[]>([]);
  const [callInProgress, setCallInProgress] = useState<number | null>(null);
  const location = useLocation<LocationState>();
  const locationRoute = location.state?.route;
  const params = useParams<{ routeId: string }>();
  const { data: fetchedRoute } = useCallCenterRoute(
    { id: params.routeId },
    { enabled: !Boolean(locationRoute) },
  );
  const route = locationRoute ?? fetchedRoute;
  const { query } = useQuery({ exclude: ["panelId"] });
  const search = getSearch({ query, routeId: params.routeId });
  const { data: orders, error, isLoading, pagination } = useCallCenterRouteOrders(search);

  useEffect(() => {
    if (route) {
      document.title = route.signature;
    }
  }, [route, isLoading, query]);

  const noResults = !orders.length && !isLoading && !error;

  if (!route) return null;

  const additionalListParams = {
    route: String(route.id),
  };

  if (error) {
    return (
      <div>
        <ListHeader additionalListParams={additionalListParams} />
        <div className="mt-4">
          <CommonError status={error._httpStatus_} />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div>
        <Loader module="callCenter" />
        <ListHeader additionalListParams={additionalListParams} />
      </div>
    );
  }

  const selectMutlipleItems = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: UUID | number,
    index: number,
    togglePanel: () => void,
  ) => {
    handleMultipleRowsHighlight(
      event,
      highlightedRows,
      id,
      index,
      setHighlightedRows,
      togglePanel,
      orders,
    );
  };

  return (
    <div className="pageContainer">
      <ListHeader additionalListParams={additionalListParams} route={route} />
      <RightPanelHandler name="callCenterRouteOrders">
        {({ close, isActive, isHighlighted, togglePanel }) => (
          <ListDrawerWrapper>
            {noResults ? (
              <NoResults on={noResults} subHeader="zamówienia" />
            ) : (
              <ModuleList>
                <ModuleListHeader
                  gridStyle={callCenterSingleRouteOrdersListConfig.grid}
                  labels={callCenterSingleRouteOrdersListConfig.labels}
                />
                <ModuleListBody>
                  {orders.map((order, index) => (
                    <ListItem
                      highlightedRows={highlightedRows}
                      index={index}
                      isActive={isActive}
                      isHighlighted={isHighlighted}
                      key={order.id}
                      order={order}
                      selectMutlipleItems={selectMutlipleItems}
                      callInProgress={callInProgress}
                      setCallInProgress={setCallInProgress}
                      togglePanel={togglePanel}
                      search={search}
                    />
                  ))}
                </ModuleListBody>
              </ModuleList>
            )}
            <RightPanel close={close} />
            <InvoicePagination pagination={pagination} />
          </ListDrawerWrapper>
        )}
      </RightPanelHandler>

      {callInProgress && (
        <CallInProgressPopUp
          callInProgressId={callInProgress}
          setCallInProgress={setCallInProgress}
        />
      )}
    </div>
  );
};
